import type { ReportEntity } from '@data/utils/types';
import { BUDGET_SECTIONS, personCost, sumCosts, type BudgetSection, type BudgetTeamMember } from './budget';
import { QUARTERS, type Quarter, type QuarterCosts, type QuarterLine, sumQuarter } from './quarterCosts';

import { readFractionalHours } from './fractional';
import { hoursByMember } from './spend';

/** One rule throughout: a sprint or a quarter counts where it starts, or it is counted twice. */

/** Hours by sprint id, then by person id: the shape both tabs hold their hours in. */
export type HoursBySprint = Record<string, Record<string, number>>;

/** What came to a sprint: reported hours plus the bought-in rows, which nobody reports time for. */
export const reportedWithBoughtIn = (
  sprints: { id: string }[],
  reportsBySprint: Record<string, ReportEntity[]>,
  productId: string,
): HoursBySprint =>
  Object.fromEntries(
    sprints.map(sprint => [
      sprint.id,
      { ...hoursByMember(reportsBySprint[sprint.id] ?? []), ...readFractionalHours('spend', productId, sprint.id) },
    ]),
  );

/** A sprint as the insights read it: its id, its number and the day it starts. */
export interface InsightSprint {
  id: string;
  number: number;
  startDate: string;
}

/** What an hour of a person's costs in a given sprint, which is the version covering it. */
export type RateAt = (sprintId: string, personId: string) => number | null;

const round = (amount: number): number => Math.round(amount * 100) / 100;

/** Somebody's hours over a set of sprints. */
const hoursOver = (sprints: InsightSprint[], hours: HoursBySprint, personId: string): number =>
  round(sprints.reduce((total, sprint) => total + (hours[sprint.id]?.[personId] ?? 0), 0));

/** What a person's hours in these sprints cost, priced sprint by sprint under the terms covering each. */
export const costOver = (
  sprints: InsightSprint[],
  hours: HoursBySprint,
  personId: string,
  rateAt: RateAt,
): number | null => {
  const priced = sprints.map(sprint => {
    const worked = hours[sprint.id]?.[personId];
    if (worked === undefined || worked === 0) return 0;
    return personCost(rateAt(sprint.id, personId), worked);
  });
  // Nobody priced is not a cost of nothing; where some sprints price, what is known is answered.
  const nothingPriced = priced.every(cost => cost === null || cost === 0);
  if (nothingPriced && priced.some(cost => cost === null)) return null;
  return sumCosts(priced);
};

/** What every person's hours in these sprints come to. */
export const workforceCost = (
  sprints: InsightSprint[],
  hours: HoursBySprint,
  people: { id: string }[],
  rateAt: RateAt,
): number => sumCosts(people.map(person => costOver(sprints, hours, person.id, rateAt)));

/** A gap in the two units it is read in: the hours, and what those hours are worth. */
export interface Gap {
  /** Budgeted less reported, so it reads the way money left over does. */
  hoursLeft: number;
  /** What those hours are worth. Null where there is no rate to price them at. */
  moneyLeft: number | null;
}

/** One person, against the plan they were given: over the whole span, and sprint by sprint. */
export interface Deviation extends Gap {
  personId: string;
  name: string;
  budgetedHours: number;
  reportedHours: number;
  /** Zero rather than absent where nothing was estimated, so a column always has a cell. */
  bySprint: Record<string, Gap>;
}

/** Whether a person was given hours in a sprint; a zero draws as a blank on the Budget grid, so it is none either. */
export const isEstimated = (budgeted: HoursBySprint, sprintId: string, personId: string): boolean =>
  (budgeted[sprintId]?.[personId] ?? 0) > 0;

/** Worst in hours first, not money; only sprints a person was estimated for count, so the unestimated are left out. */
export const deviations = (
  people: BudgetTeamMember[],
  sprints: InsightSprint[],
  budgeted: HoursBySprint,
  reported: HoursBySprint,
  rateAt: RateAt,
): Deviation[] =>
  people
    .flatMap(person => {
      const planned = sprints.filter(sprint => isEstimated(budgeted, sprint.id, person.id));
      if (!planned.length) return [];
      const budgetedHours = hoursOver(planned, budgeted, person.id);
      const reportedHours = hoursOver(planned, reported, person.id);
      // What is left of the plan, priced per sprint, so an hour missed in June is priced at June's rate.
      const leftBySprint: HoursBySprint = Object.fromEntries(
        sprints.map(sprint => [
          sprint.id,
          {
            [person.id]: planned.includes(sprint)
              ? round((budgeted[sprint.id]?.[person.id] ?? 0) - (reported[sprint.id]?.[person.id] ?? 0))
              : 0,
          },
        ]),
      );
      return [
        {
          personId: person.id,
          name: person.name,
          budgetedHours,
          reportedHours,
          hoursLeft: round(budgetedHours - reportedHours),
          moneyLeft: costOver(planned, leftBySprint, person.id, rateAt),
          // Each sprint priced on its own: a gap of nothing is worth nothing, rate or no rate.
          bySprint: Object.fromEntries(
            sprints.map(sprint => {
              const hoursLeft = leftBySprint[sprint.id][person.id];
              return [
                sprint.id,
                { hoursLeft, moneyLeft: hoursLeft === 0 ? 0 : personCost(rateAt(sprint.id, person.id), hoursLeft) },
              ];
            }),
          ),
        },
      ];
    })
    .sort((a, b) => Math.abs(b.hoursLeft) - Math.abs(a.hoursLeft) || a.name.localeCompare(b.name));

/** The first day of a quarter, which is the day it counts on. */
export const quarterStart = (year: string, quarter: Quarter): string =>
  `${year}-${String(QUARTERS.indexOf(quarter) * 3 + 1).padStart(2, '0')}-01`;

/** Every year a span touches, oldest first: a statement of work can run over a year boundary. */
export const yearsBetween = (from: string, to: string): string[] => {
  const first = Number(from.slice(0, 4));
  const last = Number(to.slice(0, 4));
  return Array.from({ length: Math.max(0, last - first + 1) }, (_, index) => String(first + index));
};

/** What one year's out of pocket costs come to inside a span, by the quarter each of them starts in. */
export const outOfPocketWithin = (year: string, lines: QuarterLine[], from: string, to: string): number =>
  sumCosts(
    QUARTERS.filter(quarter => {
      const day = quarterStart(year, quarter);
      return from <= day && day <= to;
    }).map(quarter => sumQuarter(lines, quarter)),
  );

/** How much of a statement of work is gone, and what that says about the rest of it. */
export interface SowUsage {
  /** Euros spent inside the span: the workforce priced, plus the bills that fall in it. */
  used: number;
  ceiling: number;
  /** Ceiling less used: negative is an overspend. */
  left: number;
  /** Used as a percentage of the ceiling, rounded. Past 100 when it is overspent. */
  share: number;
  /** What a closed sprint in this span has averaged, and how many were counted. */
  perSprint: number | null;
  countedSprints: number;
  /** Null where the money lasts the span, or nothing has been closed to average. */
  exhaustedOn: string | null;
}

/** Null where there is nothing to walk with, or the ceiling is already gone. */
const firstSprintPast = (remaining: InsightSprint[], left: number, perSprint: number | null): InsightSprint | null => {
  if (perSprint === null || perSprint <= 0 || left < 0) return null;
  // What each sprint in turn would have spent by the time it is over.
  const spentBy = remaining.map((_sprint, index) => round(perSprint * (index + 1)));
  return remaining[spentBy.findIndex(total => total > left)] ?? null;
};

/** The burn is over closed sprints only, and the recent few: an open one is half-reported. */
export const sowUsage = ({
  ceiling,
  spent,
  sprints,
  closedSprintIds,
  outOfPocket,
  over = 5,
  today,
}: {
  ceiling: number;
  /** What each sprint in the span cost, by sprint id. */
  spent: Record<string, number>;
  /** The span's sprints, oldest first. */
  sprints: InsightSprint[];
  closedSprintIds: string[];
  /** The bills inside the span, which are not sprint-shaped and so are not part of the burn. */
  outOfPocket: number;
  /** Today, as a day key. A sprint that has already started is not one the money is still to be spent in. */
  today?: string;
  /** How many closed sprints the average is taken over. */
  over?: number;
}): SowUsage => {
  const used = sumCosts([...sprints.map(sprint => spent[sprint.id] ?? 0), outOfPocket]);
  const left = round(ceiling - used);
  const share = ceiling === 0 ? 0 : Math.round((used / ceiling) * 100);

  const counted = sprints.filter(sprint => closedSprintIds.includes(sprint.id)).slice(-over);
  const perSprint = counted.length
    ? round(sumCosts(counted.map(sprint => spent[sprint.id] ?? 0)) / counted.length)
    : null;

  // A sprint already reporting is in `used` and must not be charged the average on top. Nor is a sprint
  // that has already begun still to come: counting those put the day the budget runs out in the past.
  const toCome = sprints.filter(
    sprint =>
      !closedSprintIds.includes(sprint.id) &&
      !((spent[sprint.id] ?? 0) > 0) &&
      (!today || sprint.startDate.slice(0, 10) >= today),
  );
  const exhausted = firstSprintPast(toCome, left, perSprint);

  return {
    used,
    ceiling,
    left,
    share,
    perSprint,
    countedSprints: counted.length,
    exhaustedOn: exhausted?.startDate ?? null,
  };
};

/** A quarter inside a span: the year it belongs to, which quarter it is, and the day it counts on. */
export interface SpanQuarter {
  year: string;
  quarter: Quarter;
  /** Its first day, which is the day it counts on. */
  startDate: string;
}

/** The quarters a span holds, oldest first. A quarter counts where it starts, as everywhere else here. */
export const quartersWithin = (from: string, to: string): SpanQuarter[] =>
  yearsBetween(from, to).flatMap(year =>
    QUARTERS.map(quarter => ({ year, quarter, startDate: quarterStart(year, quarter) })).filter(
      each => from <= each.startDate && each.startDate <= to,
    ),
  );

/** What a quarter of a span is keyed by on a cost line, since a span can hold two of the same one. */
export const spanQuarterKey = (quarter: SpanQuarter): string => `${quarter.year}-${quarter.quarter}`;

/** One out of pocket line inside a span: what it came to in each quarter of it, and over the whole. */
export interface OutOfPocketLine {
  section: BudgetSection;
  name: string;
  /** EUR by `spanQuarterKey`. A quarter is absent where nothing was entered, which is not nothing spent. */
  amounts: Record<string, number>;
  total: number;
}

/** Matched on name across the years a span touches: one bill from October into January is one line. */
export const outOfPocketLines = (
  quarters: SpanQuarter[],
  costsByYear: Record<string, QuarterCosts>,
): OutOfPocketLine[] => {
  const rows = new Map<string, OutOfPocketLine>();

  quarters.forEach(each => {
    const costs = costsByYear[each.year];
    if (!costs) return;
    BUDGET_SECTIONS.forEach(section => {
      costs[section].forEach(line => {
        const amount = line.amounts[each.quarter];
        // Nothing entered is not an entry of nothing: a line the quarter says nothing about stays blank.
        if (amount === undefined) return;
        const name = line.name.trim();
        const key = `${section}:${name.toLowerCase()}`;
        const row = rows.get(key) ?? { section, name, amounts: {}, total: 0 };
        const at = spanQuarterKey(each);
        row.amounts[at] = round((row.amounts[at] ?? 0) + amount);
        row.total = round(row.total + amount);
        rows.set(key, row);
      });
    });
  });

  return [...rows.values()].sort(
    (a, b) => BUDGET_SECTIONS.indexOf(a.section) - BUDGET_SECTIONS.indexOf(b.section) || a.name.localeCompare(b.name),
  );
};
